import React, { useState, useCallback, useRef } from 'react';
import { datasetAPI, DatasetUploadResult } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { GUSDataType } from '../types/gusSchemas';

interface DataUploadProps {
  tenantId?: string;
  projectId?: string;
  onUploadComplete?: (dataset: DatasetUploadResult) => void;
}

// GUS-specific data type definitions with schema requirements
const GUS_DATA_TYPES = [
  {
    value: GUSDataType.CITIZEN_SATISFACTION,
    label: 'Citizen Satisfaction',
    description: 'Hexagon-based satisfaction scores by topic',
    fileType: 'GeoJSON (Polygon)',
    requiredFields: ['TOPIC', 'overall_score', 'total', 'pub_year'],
    optionalFields: ['HEX8', 'HEX9', 'NEIGHBORHENAME', 'very_satisfied', 'satisfied'],
    exampleFile: 'Topics_HEX8.geojson',
  },
  {
    value: GUSDataType.DEMOGRAPHICS,
    label: 'Demographics & Employment',
    description: 'Population and employment statistics',
    fileType: 'GeoJSON (Polygon)',
    requiredFields: ['HEX'],
    optionalFields: ['NEIGHBORHE', 'POP22_COUN', 'GFAtotal', 'empJOB', 'pop_density'],
    exampleFile: 'population_and_employment_HEX8.geojson',
  },
  {
    value: GUSDataType.POI_ACTIVITY,
    label: 'Points of Interest & Activity',
    description: 'Location-based POI and activity data',
    fileType: 'GeoJSON (Point)',
    requiredFields: ['post_id'],
    optionalFields: ['name', 'category', 'primary_space_type', 'activities', 'amenities', 'rating'],
    exampleFile: 'riyadh_activity_googlemaps.geojson',
  },
  {
    value: GUSDataType.URBAN_SPACES,
    label: 'Urban Spaces',
    description: 'Classified urban features and public spaces',
    fileType: 'GeoJSON (Polygon)',
    requiredFields: ['space_id', 'space_type'],
    optionalFields: ['name', 'area_sqm', 'neighborhood', 'ownership', 'features'],
    exampleFile: 'urban_spaces.geojson',
  },
  {
    value: GUSDataType.MOBILITY,
    label: 'Mobility Data',
    description: 'Origin-destination matrices and traffic flows',
    fileType: 'GeoJSON (LineString) or CSV',
    requiredFields: ['origin_zone', 'destination_zone', 'trip_count'],
    optionalFields: ['avg_travel_time', 'avg_distance_km', 'time_period', 'mode_car_percent'],
    exampleFile: 'tomtom_od_matrix.geojson',
  },
];

export const DataUpload: React.FC<DataUploadProps> = ({
  tenantId,
  projectId,
  onUploadComplete,
}) => {
  const { showNotification } = useNotification();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDataType, setSelectedDataType] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadResult, setUploadResult] = useState<DatasetUploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get the selected data type configuration
  const selectedTypeConfig = GUS_DATA_TYPES.find(type => type.value === selectedDataType);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelection = (selectedFile: File) => {
    setFile(selectedFile);
    if (!name) {
      // Auto-populate name from filename (without extension)
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
      setName(nameWithoutExt);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      showNotification('Please select a file to upload', 'error');
      return;
    }

    if (!name.trim()) {
      showNotification('Please provide a name for the dataset', 'error');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const result = await datasetAPI.upload(
        file,
        name.trim(),
        tenantId,
        projectId,
        description.trim() || undefined,
        selectedDataType || undefined,
        undefined  // expectedFileType is auto-detected from data type
      );

      setUploadResult(result);

      if (result.status === 'success') {
        showNotification(`Dataset "${name}" uploaded successfully!`, 'success');

        // Reset form
        setFile(null);
        setName('');
        setDescription('');
        setSelectedDataType('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        if (onUploadComplete) {
          onUploadComplete(result);
        }
      } else if (result.status === 'validation_failed') {
        showNotification('File validation failed. Please check the errors below.', 'error');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      showNotification(error.response?.data?.detail || 'Failed to upload dataset', 'error');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="data-upload">
      <h2>Upload Dataset</h2>

      {/* Drag and Drop Area */}
      <div
        className={`upload-dropzone ${dragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileInput}
          accept=".geojson,.json,.csv"
          style={{ display: 'none' }}
        />

        {file ? (
          <div className="file-info">
            <div className="file-icon">📄</div>
            <div className="file-details">
              <div className="file-name">{file.name}</div>
              <div className="file-size">{formatFileSize(file.size)}</div>
            </div>
            <button
              type="button"
              className="remove-file"
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="upload-prompt">
            <div className="upload-icon">☁️</div>
            <p>Drag and drop file here, or click to browse</p>
            <p className="upload-hint">
              Supported formats: GeoJSON, CSV, JSON
            </p>
          </div>
        )}
      </div>

      {/* Dataset Metadata Form */}
      <div className="upload-form">
        <div className="form-group">
          <label htmlFor="dataset-name">
            Dataset Name <span className="required">*</span>
          </label>
          <input
            id="dataset-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Riyadh Satisfaction Data 2024"
            disabled={uploading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dataset-description">Description</label>
          <textarea
            id="dataset-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the dataset and its purpose..."
            rows={3}
            disabled={uploading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="data-type">
            Data Type <span className="required">*</span>
          </label>
          <select
            id="data-type"
            value={selectedDataType}
            onChange={(e) => setSelectedDataType(e.target.value)}
            disabled={uploading}
            className="data-type-select"
          >
            <option value="">-- Select GUS Data Type --</option>
            {GUS_DATA_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label} - {type.description}
              </option>
            ))}
          </select>

          {selectedTypeConfig && (
            <div className="schema-requirements">
              <div className="schema-section">
                <div className="schema-header">
                  <strong>Expected Format:</strong> {selectedTypeConfig.fileType}
                </div>
                <div className="schema-header">
                  <strong>Example:</strong> {selectedTypeConfig.exampleFile}
                </div>
              </div>

              <div className="schema-section">
                <div className="schema-label">Required Fields:</div>
                <div className="field-tags">
                  {selectedTypeConfig.requiredFields.map((field) => (
                    <span key={field} className="field-tag required">
                      {field}
                    </span>
                  ))}
                </div>
              </div>

              <div className="schema-section">
                <div className="schema-label">Optional Fields:</div>
                <div className="field-tags">
                  {selectedTypeConfig.optionalFields.slice(0, 6).map((field) => (
                    <span key={field} className="field-tag optional">
                      {field}
                    </span>
                  ))}
                  {selectedTypeConfig.optionalFields.length > 6 && (
                    <span className="field-tag more">
                      +{selectedTypeConfig.optionalFields.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!file || !name.trim() || !selectedDataType || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Dataset'}
          </button>
          {!selectedDataType && (
            <p className="upload-hint">Please select a data type to continue</p>
          )}
        </div>
      </div>

      {/* Upload Result Display */}
      {uploadResult && (
        <div className={`upload-result ${uploadResult.status === 'success' ? 'success' : 'error'}`}>
          {uploadResult.status === 'success' ? (
            <div className="result-success">
              <h3>✓ Upload Successful</h3>
              <div className="result-details">
                <p>
                  <strong>File Size:</strong> {uploadResult.file_size_mb?.toFixed(2)} MB
                </p>
                {uploadResult.validation_result && (
                  <>
                    <p>
                      <strong>Detected Type:</strong>{' '}
                      {uploadResult.validation_result.detected_type}
                    </p>
                    <p>
                      <strong>Category:</strong>{' '}
                      {uploadResult.validation_result.detected_category}
                    </p>
                    {uploadResult.validation_result.detected_schema && (
                      <details className="schema-details">
                        <summary>View Schema Details</summary>
                        <pre>
                          {JSON.stringify(
                            uploadResult.validation_result.detected_schema,
                            null,
                            2
                          )}
                        </pre>
                      </details>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="result-error">
              <h3>✗ Validation Failed</h3>
              {uploadResult.validation_result?.validation_errors && (
                <ul className="error-list">
                  {uploadResult.validation_result.validation_errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              )}
              {uploadResult.validation_result?.warnings &&
                uploadResult.validation_result.warnings.length > 0 && (
                  <>
                    <h4>Warnings:</h4>
                    <ul className="warning-list">
                      {uploadResult.validation_result.warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </>
                )}
            </div>
          )}
        </div>
      )}

      
    </div>
  );
};

export default DataUpload;
